import { Dictionary } from "lodash";
import {
	ActivityResultsPayload,
	DonutChartDTO,
	EmissionsChainTransportResultsDTO,
	EmissionsHaulageTransportResultsDTO,
	EmissionsIntervalResultsDTO,
	EmissionsTransportsResultsDTO,
	EnvironmentalKpiDTO,
	ResultsBasePayload,
} from "../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { useReadResource, useWriteResource } from "../../../hooks/useResource";
import { TransportPerspective } from "../../../state/preferences";

interface Options {
	payload: ResultsBasePayload;
	perspective?: TransportPerspective;
	periodType?: "month" | "quarter" | "four-month" | "semester";
	tab: "overview" | "greenrouter" | "thirdparty" | "greenrouterUnits";
	isLoadingFromStorage?: boolean;
}

// IMPORTANT TYPE DEFINED FOR PAGE DYNAMIC FILE DOWNLOAD
type PdfResourceType = ReturnType<typeof useWriteResource<ResultsBasePayload, File>>;

export interface TransportFileDownload {
	pdfTransport: PdfResourceType;
}

export function useTransportData({ payload, periodType, perspective = "operational", tab }: Options) {
	const tabQueries: Dictionary<Array<string>> = {
		overview: ["transport", "intervals"],
		greenrouter: ["transport", "intervals", "typeTransport", "chainTransport"],
		thirdparty: [
			"transport",
			"charts",
			"environmentalKpi",
			"sitesSource",
			"typeTransportThirdParty",
			"chainTransportThirdParty",
		],
	};

	const transport = useReadResource<ResultsBasePayload, EmissionsTransportsResultsDTO>(
		{ route: ApiRoutes.ResultsTransportEmission, query: { perspective } },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("transport"),
		},
		payload
	);

	const intervals = useReadResource<ResultsBasePayload, EmissionsIntervalResultsDTO>(
		{ route: ApiRoutes.ResultsEmissionsIntervals, query: { activity: "transport", perspective } },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("intervals"),
		},
		{ ...payload, period: periodType }
	);

	const typeTransport = useReadResource<ResultsBasePayload, EmissionsHaulageTransportResultsDTO>(
		{ route: ApiRoutes.ResultsEmissionType, query: { perspective } },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("typeTransport"),
		},
		payload
	);

	const chainTransport = useReadResource<ResultsBasePayload, EmissionsChainTransportResultsDTO>(
		{ route: ApiRoutes.ResultsChainTransportEmission, query: { perspective } },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("chainTransport"),
		},
		payload
	);

	const calculationMethods = useReadResource<ActivityResultsPayload, DonutChartDTO>(
		{ route: ApiRoutes.ResultThirdPartyCalculationMethods },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("charts"),
		},
		{ ...payload, period: periodType, activity: "transport" }
	);

	const nodesCertification = useReadResource<ActivityResultsPayload, DonutChartDTO>(
		{ route: ApiRoutes.ResultThirdPartyNodesCertification },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("charts"),
		},
		{ ...payload, period: periodType, activity: "transport" }
	);

	const environmentalKpi = useReadResource<ActivityResultsPayload, EnvironmentalKpiDTO>(
		{ route: ApiRoutes.ResultThirdPartyEnvironmentalKpi, query: { perspective } },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("environmentalKpi"),
		},
		{ ...payload, period: periodType, dataOrigin: "third-party", activity: "sites" }
	);

	const typeTransportThirdParty = useReadResource<ResultsBasePayload, EmissionsHaulageTransportResultsDTO>(
		{ route: ApiRoutes.ResultsEmissionType },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("typeTransportThirdParty"),
		},
		{ ...payload, dataOrigin: "third-party" }
	);

	const chainTransportThirdParty = useReadResource<ResultsBasePayload, EmissionsChainTransportResultsDTO>(
		{ route: ApiRoutes.ResultsChainTransportEmission },
		{
			readMethod: "post",
			disabled: !tabQueries[tab].includes("chainTransportThirdParty"),
		},
		{ ...payload, dataOrigin: "third-party" }
	);

	const pdfTransport = useWriteResource<ResultsBasePayload, File>(
		{ route: ApiRoutes.ResultsTranposrtPDFDownload },
		{ subjectKey: "download" }
	);

	return {
		emissions: {
			transport,
			intervals,
			typeTransport,
			chainTransport,
		},
		thirdParty: {
			calculationMethods,
			nodesCertification,
			environmentalKpi,
			typeTransport: typeTransportThirdParty,
			chainTransport: chainTransportThirdParty,
		},
		fileDownload: {
			pdfTransport,
		},
	};
}
